import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip, Alert, LinearProgress } from '@mui/material';
import { Add, Visibility } from '@mui/icons-material';
import api from '../../services/api';

const statusLabels = {
  planung: { label: 'Planung', color: 'default' },
  aktiv: { label: 'Aktiv', color: 'success' },
  pausiert: { label: 'Pausiert', color: 'warning' },
  abgeschlossen: { label: 'Abgeschlossen', color: 'info' },
  abgebrochen: { label: 'Abgebrochen', color: 'error' },
};

const ProjektePage = () => {
  const navigate = useNavigate();
  const [projekte, setProjekte] = useState([]);
  const [foerdergeber, setFoerdergeber] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    titel: '', foerdergeberId: '', foerderkennzeichen: '', bewilligteSumme: '',
    laufzeitVon: '', laufzeitBis: '', beschreibung: ''
  });

  const load = async () => {
    try {
      const [pRes, fgRes] = await Promise.all([api.get('/projekte'), api.get('/foerdergeber')]);
      setProjekte(pRes.data);
      setFoerdergeber(fgRes.data);
    } catch (err) { setError('Fehler beim Laden'); }
  };

  useEffect(() => { load(); }, []);

  const handleOpen = () => {
    setForm({ titel: '', foerdergeberId: '', foerderkennzeichen: '', bewilligteSumme: '',
      laufzeitVon: '', laufzeitBis: '', beschreibung: '' });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const data = { ...form };
      if (!data.bewilligteSumme) delete data.bewilligteSumme;
      if (!data.laufzeitVon) delete data.laufzeitVon;
      if (!data.laufzeitBis) delete data.laufzeitBis;
      await api.post('/projekte', data);
      setOpen(false);
      load();
    } catch (err) { setError('Fehler beim Speichern'); }
  };

  const formatCurrency = (val) => val ? parseFloat(val).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '-';

  const calcProgress = (von, bis) => {
    if (!von || !bis) return 0;
    const start = new Date(von).getTime();
    const end = new Date(bis).getTime();
    const now = Date.now();
    if (now <= start) return 0;
    if (now >= end) return 100;
    return Math.round(((now - start) / (end - start)) * 100);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="#1B5E20">Projekte</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpen}
          sx={{ bgcolor: '#1B5E20', '&:hover': { bgcolor: '#2E7D32' } }}>
          Neues Projekt
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Titel</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fördergeber</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Budget</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Fortschritt</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Laufzeit</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projekte.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#999' }}>Noch keine Projekte vorhanden</TableCell></TableRow>
            ) : projekte.map((p) => {
              const progress = calcProgress(p.laufzeitVon, p.laufzeitBis);
              return (
                <TableRow key={p.id} hover>
                  <TableCell>{p.titel}</TableCell>
                  <TableCell>{p.foerdergeber?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip label={statusLabels[p.status]?.label || p.status} color={statusLabels[p.status]?.color || 'default'} size="small" />
                  </TableCell>
                  <TableCell>{formatCurrency(p.bewilligteSumme)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress variant="determinate" value={progress}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4,
                          bgcolor: '#E8F5E9', '& .MuiLinearProgress-bar': { bgcolor: '#1B5E20' } }} />
                      <Typography variant="caption" color="text.secondary">{progress}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(p.laufzeitVon)} - {formatDate(p.laufzeitBis)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => navigate(`/projekte/${p.id}`)} sx={{ color: '#1B5E20' }}>
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neues Projekt</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Titel *" value={form.titel} onChange={(e) => setForm({...form, titel: e.target.value})} margin="normal" />
          <TextField fullWidth select label="Fördergeber *" value={form.foerdergeberId} onChange={(e) => setForm({...form, foerdergeberId: e.target.value})} margin="normal">
            {foerdergeber.map((fg) => <MenuItem key={fg.id} value={fg.id}>{fg.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Förderkennzeichen" value={form.foerderkennzeichen} onChange={(e) => setForm({...form, foerderkennzeichen: e.target.value})} margin="normal" />
          <TextField fullWidth label="Bewilligte Summe (EUR)" type="number" value={form.bewilligteSumme}
            onChange={(e) => setForm({...form, bewilligteSumme: e.target.value})} margin="normal" />
          <TextField fullWidth label="Laufzeit von" type="date" value={form.laufzeitVon}
            onChange={(e) => setForm({...form, laufzeitVon: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Laufzeit bis" type="date" value={form.laufzeitBis}
            onChange={(e) => setForm({...form, laufzeitBis: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Beschreibung" value={form.beschreibung}
            onChange={(e) => setForm({...form, beschreibung: e.target.value})} margin="normal" multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjektePage;
